/* TODO: revise imports to prevent dependency cycle */
/* eslint-disable import/no-cycle */

import React, { useEffect, useState } from 'react';
import BaseTable, { Column } from 'react-base-table';
import distinctColors from 'distinct-colors';
import 'react-base-table/styles.css';
import FuzzySearch from 'fuzzy-search';
import { Button } from 'react-bootstrap';
import {
  Locations,
  FridgeSoftware,
  FridgeSoftwarePilots,
  UpdateFridgeArtifacts,
  PutFridgeSoftwarePilot,
} from '../../libs/GraphQL';
import styledComponent from '../../utils/styledComponent';
import FilterModal from './FilterModal';
import ConfirmationModal from './ConfirmationModal';
import ReceiptModal from './ReceiptModal';
import FilterClearButton from './FilterClearButton';
import {
  cleanVersion,
  getFilterDescription,
  sortArrayOfObjects,
  sortOnPilots,
  getFilteredData,
  isCorrectAWSDate,
} from './Utilities/ViewFridgeSoftware_utilities';
import Loader from '../../components/atoms/Loader';
import { PilotsHeader, PilotsColumn } from './Utilities/PilotsRenderer';
import DeploymentToolbar from './DeploymentToolbar';

/*     STYLED COMPONENT     */

const StyledDiv = styledComponent('div', {
  '.row-style-one.BaseTable__row': {
    backgroundColor: '#F4F4F4',
  },
  '.row-style-two.BaseTable__row': {
    backgroundColor: '#EEEEEE',
  },
  '.inventoryHeader': {
    width: '1302px',
    height: '60px',
    fontWeight: '500',
    fontSize: '40px',
    lineHeight: '60px',
    color: 'rgba(0, 0, 0, 0.87)',
    flex: 'none',
    order: '0',
    flexGrow: '1',
  },
  '.toolBar': {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    padding: '16px',
    gap: '40px',
    width: '1145px',
    height: '50px',
    background: '#FFFFFF',
    flex: 'none',
    order: '2',
    alignSelf: 'stretch',
    flexGrow: '0',
    marginBottom: '10px',
    paddingLeft: '0px',
  },
  '&.inputWrapper': {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  '&.filterWrapper': {
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'row',
    alignItems: 'center',
  },
  '&.buttonWrapper': {
    display: 'flex',
  },
  '&:nth-of-type(even)': {
    background: '#f5f5f5',
  },
});

// Statuses for no filter on
export const BASE_STATUSES = {
  LIVE: false,
  DEAD: false,
  OFFLINE: false,
};

// Statuses to default to when user opens page
export const DEFAULT_STATUSES = {
  LIVE: true,
  DEAD: false,
  OFFLINE: false,
};

// States for sorting
const DEFAULT_SORT = {
  pilots: null,
  fridgeName: null,
  FRIDGE_UI_APPLICATION: null,
  FRIDGE_JAVA_SERVER: null,
  FRIDGE_AGENT: null,
  FRIDGE_INVENTORY: null,
  FRIDGE_PRICING_PARSER: null,
  FRIDGE_PRICING_CONSUMER: null,
  FRIDGE_HEARTBEAT: null,
  FRIDGE_JAVA_SERVER_CONFIG: null,
};

const TABLE_ARTIFACT_COLUMNS = {
  FRIDGE_UI_APPLICATION: 'UI',
  FRIDGE_JAVA_SERVER: 'Java Server',
  FRIDGE_AGENT: 'Agent',
  FRIDGE_INVENTORY: 'Inventory',
  FRIDGE_PRICING_PARSER: 'Parser',
  FRIDGE_PRICING_CONSUMER: 'Consumer',
  FRIDGE_HEARTBEAT: 'Heartbeat',
  FRIDGE_JAVA_SERVER_CONFIG: 'Java Config',
};

const date = new Date();
const year = date.getFullYear().toString();
const month =
  date.getMonth() + 1 < 10
    ? `0${(date.getMonth() + 1).toString()}`
    : (date.getMonth() + 1).toString();
const day =
  date.getDate() < 10
    ? `0${date.getDate().toString()}`
    : date.getDate().toString();

const NEW_DEPLOYMENT = {
  deployment: {
    selectedFridges: new Set(),
    selectedSoftware: null,
    selectedVersion: null,
  },
  pilot: {
    isPilot: false,
    startDate: `${year}-${month}-${day}`,
  },
};

/*     VIEWFRIDGESOFTWARE COMPONENT     */

const ViewFridgeSoftware = () => {
  /*     STATE VARIABLES     */

  const [loading, setLoading] = useState(true);
  const [queryError, setQueryError] = useState(false);

  const [statuses, setStatuses] = useState({ ...BASE_STATUSES });
  const [selectedSoftware, setSelectedSoftware] = useState({});
  const [selectedVersions, setSelectedVersions] = useState([]);
  const [selectedPilot, setSelectedPilot] = useState({});

  const [locationData, setLocationData] = useState([]);
  const [softwareData, setSoftwareData] = useState([]);
  const [pilotsData, setPilotsData] = useState([]);

  const [allTableData, setAllTableData] = useState({});
  const [filteredData, setFilteredData] = useState({});
  const [shownTableData, setShownTableData] = useState([]);

  const [sortState, setSortState] = useState({ ...DEFAULT_SORT });

  const [showFilter, setShowFilter] = useState(false);
  const [showClearButton, setShowClearButton] = useState(false);
  const [filterResponse, setFilterResponse] = useState(null);
  const [filterDescription, setFilterDescription] = useState('FILTER');

  const [multiplePilotFridges, setMultiplePilotFridges] = useState(new Set());

  const [showDeploymentToolbar, setShowDeploymentToolbar] = useState(false);
  const [deploymentSelectedFridges, setDeploymentSelectedFridges] = useState(
    new Set(),
  );
  const [deploymentSelectedSoftware, setDeploymentSelectedSoftware] =
    useState(null);
  const [deploymentVersionOptions, setDeploymentVersionOptions] = useState([]);
  const [deploymentIsPilot, setDeploymentIsPilot] = useState(false);
  const [deploymentInfo, setDeploymentInfo] = useState({ ...NEW_DEPLOYMENT });
  const [isDeployDisabled, setIsDeployDisabled] = useState(true);

  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const [deploymentResponse, setDeploymentResponse] = useState({});

  const [showDeploymentReceipt, setShowDeploymentReceipt] = useState(false);

  const [incorrectFridgeNames, setIncorrectFridgeNames] = useState([]);
  const [failedFridges, setFailedFridges] = useState([]);

  const [searchbarValue, setSearchbarValue] = useState('');
  const searcher = new FuzzySearch(
    Object.values(filteredData),
    ['fridgeName'],
    {
      caseSensitive: false,
    },
  );

  /*     FUNCTIONS TO BE PASSED AS PROPS     */

  const applyFilterResponse = (newResponse) => {
    setFilterResponse(newResponse);
    setSearchbarValue('');
  };

  /*     FUNCTIONS      */

  const clearFilter = (newMessage) => {
    // Parameter should be set to null if reset filters
    if (!newMessage) {
      setStatuses({ ...BASE_STATUSES });
      setSelectedSoftware({});
      setSelectedVersions([]);
      setSelectedPilot({});
      setFilterResponse(null);
      setSearchbarValue('');
    }
  };

  const initializeUI = async () => {
    // Retrieve FridgeSoftwarePilots from query
    // onlyShowActive refers to which pilots we want to recieve
    const onlyShowActivePilots = true;
    FridgeSoftwarePilots(onlyShowActivePilots)
      .then((response) => {
        const pallette = distinctColors({
          count: response.data.getFridgeSoftwarePilots.length,
          chromaMin: 30,
        });

        const allPilotIds = new Set();
        const fridgesWithMultiplePilots = new Set();
        let index = 0;

        response.data.getFridgeSoftwarePilots.map((pilot) => {
          pilot.iconColor = pallette[index];
          index++;

          pilot.fridgeIds.map((id) => {
            if (allPilotIds.has(id)) {
              fridgesWithMultiplePilots.add(id);
            }
            allPilotIds.add(id);
          });
        });

        setMultiplePilotFridges(fridgesWithMultiplePilots);
        setPilotsData(response.data.getFridgeSoftwarePilots);
      })
      .catch((e) => {
        console.log(e);
        setLoading(false);
        setQueryError(true);
      });

    // Retrieve locations from query
    const locationsResult = await Locations({}, true).catch((e) => {
      console.log(e);
      setLoading(false);
      setQueryError(true);
    });

    // Convert response to dict of id: status associations
    if (locationsResult) {
      setLocationData(locationsResult);
    }

    // Retrieve artifactBreakdowns from query
    const softwareResult = await FridgeSoftware().catch((e) => {
      console.log(e);
      setLoading(false);
      setQueryError(true);
    });

    if (softwareResult) {
      // Clean version names
      softwareResult.data.getFridgeArtifacts.map((artifactBreakdown) =>
        artifactBreakdown.versions.map((version) => {
          const cleanName = cleanVersion(
            artifactBreakdown.artifactType,
            version.versionName,
          );
          version.cleanName = cleanName;
        }),
      );
      setSoftwareData(softwareResult.data.getFridgeArtifacts);

      // HERE IS WHERE INITIAL FILTER IS SET
      setFilterResponse({
        statuses: { ...DEFAULT_STATUSES },
        selectedSoftware: {},
        selectedVersions: [],
        selectedPilot: {},
      });
      setStatuses({ ...DEFAULT_STATUSES });
    }
  };

  const getAllTableData = () => {
    const entries = {};

    // Use location data
    locationData.map(
      // eslint-disable-next-line no-return-assign
      (location) =>
        (entries[location.id] = {
          fridgeName: location.id,
          status: location.status.ops,
        }),
    );

    // Use software artifact data
    softwareData.map((artifactBreakdown) =>
      artifactBreakdown.versions.map((version) => {
        version.fridgesDeployedTo.map((fridge) => {
          if (fridge in entries) {
            entries[fridge][artifactBreakdown.artifactType] = version.cleanName;
          }
        });
      }),
    );

    setAllTableData(entries);
    setFilteredData(entries);

    if (entries && Object.keys(entries).length > 0) {
      setLoading(false);
    }
  };

  const updateFilteredData = () => {
    if (filterResponse) {
      setFilteredData(getFilteredData(allTableData, filterResponse));
    }
  };

  const rowClassName = React.useCallback(({ rowIndex }) => {
    // we sliced the original data by 1, so we have to correct the index back
    if (rowIndex < 0 || (rowIndex + 1) % 2 === 0) return 'row-style-one';
    return 'row-style-two';
  });

  const onColumnSort = ({ key, order }) => {
    // Handle special case if pilot column
    let sortedData = [];
    if (key === 'pilots') {
      sortedData = sortOnPilots(shownTableData, order, pilotsData);
    } else {
      sortedData = sortArrayOfObjects(shownTableData, key, order);
    }

    const newSortState = {};
    Object.keys(sortState).map((columnKey) => {
      if (columnKey === key) {
        newSortState[key] = sortState[key] === 'desc' ? null : order;
      } else {
        newSortState[key] = null;
      }
    });
    setSortState(newSortState);
    setShownTableData(sortedData);
  };

  const headerPilotsRenderer = () => {
    return (
      <di>
        <PilotsHeader multiplePilotFridges={multiplePilotFridges} />
      </di>
    );
  };

  const pilotsColumnRenderer = ({ rowData }) => {
    return (
      <div>
        <PilotsColumn rowData={rowData} pilotsData={pilotsData} />
      </div>
    );
  };

  const headerCheckboxRenderer = () => {
    let checked = true;

    shownTableData.forEach((entry) => {
      if (!deploymentSelectedFridges.has(entry.fridgeName)) {
        checked = false;
      }
    });

    if (shownTableData.length === 0) {
      checked = false;
    }

    return (
      <input
        key="headerCheckbox"
        id="headerCheckbox"
        type="checkbox"
        checked={checked}
        onChange={(e) => {
          const updatedItemsChecked = [];
          shownTableData.forEach((item) => {
            updatedItemsChecked.push(item.fridgeName);
          });
          const newSelected = new Set();
          deploymentSelectedFridges.forEach((item) => {
            newSelected.add(item);
          });

          if (!checked) {
            updatedItemsChecked.map((id) => {
              newSelected.add(id);
            });
          } else {
            updatedItemsChecked.map((id) => {
              newSelected.delete(id);
            });
          }
          setDeploymentSelectedFridges(newSelected);
          setDeploymentInfo({
            ...deploymentInfo,
            deployment: {
              ...deploymentInfo.deployment,
              selectedFridges: newSelected,
            },
          });
        }}
      />
    );
  };

  const checkboxRenderer = ({ rowData }) => {
    return (
      <input
        key={rowData.fridgeName}
        id={rowData.fridgeName}
        type="checkbox"
        checked={deploymentSelectedFridges.has(rowData.fridgeName)}
        onChange={(e) => {
          const newSelected = new Set(deploymentSelectedFridges);
          if (!newSelected.has(e.target.id)) {
            newSelected.add(rowData.fridgeName);
          } else {
            newSelected.delete(rowData.fridgeName);
          }
          setDeploymentSelectedFridges(newSelected);
          setDeploymentInfo({
            ...deploymentInfo,
            deployment: {
              ...deploymentInfo.deployment,
              selectedFridges: newSelected,
            },
          });
        }}
      />
    );
  };

  const startCreateDeployment = () => {
    setDeploymentSelectedFridges(new Set());
    setDeploymentSelectedSoftware(null);
    setDeploymentVersionOptions([]);
    setDeploymentIsPilot(null);
    setDeploymentInfo({ ...NEW_DEPLOYMENT });
    setDeploymentResponse({});
  };

  const deploySoftware = async () => {
    setShowDeploymentToolbar(false);
    setLoading(true);

    // Confirm each fridge is a correct fridgeId
    const verifiedFridges = [];
    Array.from(deploymentInfo.deployment.selectedFridges).map((id) => {
      if (id in allTableData) {
        verifiedFridges.push(id);
      }
    });

    const isDeployment =
      deploymentInfo.deployment.selectedSoftware &&
      deploymentInfo.deployment.selectedVersion;

    let softwareResponse = '';

    if (isDeployment) {
      softwareResponse = await UpdateFridgeArtifacts(
        deploymentInfo.deployment.selectedSoftware,
        deploymentInfo.deployment.selectedVersion.rawVersion,
        verifiedFridges,
      ).catch((e) => {
        console.log(e);
        setDeploymentResponse({ deployment: e.errors[0].message });
      });
    }
    // The following if will pass if !isDeployment or the deployment was successful
    if (typeof softwareResponse !== 'undefined') {
      // This is only if the deployment happened, not for just pilot scenarios
      if (isDeployment) {
        if (
          verifiedFridges.length !==
          softwareResponse.data.updateFridgeArtifacts.length
        ) {
          // eslint-disable-next-line no-shadow
          let failedFridges = [...verifiedFridges];
          softwareResponse.data.updateFridgeArtifacts.map((artifactObject) => {
            const fridgeName = artifactObject.pk.split('#software')[0];
            failedFridges = failedFridges.filter(
              (fridge) => fridge !== fridgeName,
            );
          });

          setFailedFridges(failedFridges);
          setDeploymentResponse({
            deployment: 'The following fridges failed to update: ',
          });
        } else if (deploymentInfo.pilot.isPilot) {
          const verifiedPilot = { ...deploymentInfo.pilot };
          delete verifiedPilot.isPilot;
          verifiedPilot.active = true;
          verifiedPilot.fridgeIds = verifiedFridges;

          const pilotResponse = await PutFridgeSoftwarePilot(
            verifiedPilot,
          ).catch((e) => {
            console.log(e);
            setDeploymentResponse({
              ...deploymentResponse,
              deployment: 'success',
              pilot: e.errors[0].message,
            });
          });

          if (typeof pilotResponse !== 'undefined') {
            if (pilotResponse.data.putFridgeSoftwarePilot) {
              setDeploymentResponse({
                ...deploymentResponse,
                deployment: 'success',
                pilot: 'success',
              });
            } else {
              setDeploymentResponse({
                ...deploymentResponse,
                deployment: 'success',
                pilot: 'The PutItem operation failed in the lambda function.',
              });
            }
          }
        } else {
          setDeploymentResponse({ deployment: 'success', pilot: 'N/A' });
        }
      } else if (deploymentInfo.pilot.isPilot) {
        const verifiedPilot = { ...deploymentInfo.pilot };
        delete verifiedPilot.isPilot;
        verifiedPilot.active = true;
        verifiedPilot.fridgeIds = verifiedFridges;

        const pilotResponse = await PutFridgeSoftwarePilot(verifiedPilot).catch(
          (e) => {
            console.log(e);
            setDeploymentResponse({
              ...deploymentResponse,
              deployment: 'N/A',
              pilot: e.errors[0].message,
            });
          },
        );

        if (typeof pilotResponse !== 'undefined') {
          if (pilotResponse.data.putFridgeSoftwarePilot) {
            setDeploymentResponse({
              ...deploymentResponse,
              deployment: 'N/A',
              pilot: 'success',
            });
          } else {
            setDeploymentResponse({
              ...deploymentResponse,
              deployment: 'N/A',
              pilot: 'The PutItem operation failed in the lambda function.',
            });
          }
        }
      }
    }

    setIsDeployDisabled(true);
    initializeUI().then(() => {
      // Reset filters and searchbar
      setFilterResponse({
        statuses: { ...DEFAULT_STATUSES },
        selectedSoftware: {},
        selectedVersions: [],
        selectedPilot: {},
      });
      setStatuses({ ...DEFAULT_STATUSES });
      setSelectedSoftware({});
      setSelectedVersions([]);
      setSelectedPilot({});
      setSearchbarValue('');

      setLoading(false);
      setShowDeploymentReceipt(true);
    });
  };

  const disableDeploy = () => {
    const deployCompleted =
      !(
        deploymentSelectedFridges.size === 0 ||
        incorrectFridgeNames.length > 0 ||
        (deploymentSelectedFridges.size === 1 &&
          Array.from(deploymentSelectedFridges)[0] === '')
      ) &&
      deploymentInfo.deployment.selectedSoftware &&
      deploymentInfo.deployment.selectedVersion;
    const pilotCompleted =
      'id' in deploymentInfo.pilot &&
      deploymentInfo.pilot.id !== '' &&
      'displayName' in deploymentInfo.pilot &&
      deploymentInfo.pilot.displayName !== '' &&
      'stage' in deploymentInfo.pilot &&
      Number.isInteger(deploymentInfo.pilot.stage) &&
      'startDate' in deploymentInfo.pilot &&
      isCorrectAWSDate(deploymentInfo.pilot.startDate) &&
      'endDate' in deploymentInfo.pilot &&
      isCorrectAWSDate(deploymentInfo.pilot.endDate) &&
      'description' in deploymentInfo.pilot &&
      deploymentInfo.pilot.description !== '';
    const deployStarted =
      deploymentInfo.deployment.selectedSoftware ||
      deploymentInfo.deployment.selectedVersion;

    // Just deployment
    if (deployCompleted && !deploymentIsPilot) {
      return false;
    }

    // Just pilot
    if (deploymentIsPilot && pilotCompleted && !deployStarted) {
      return false;
    }

    // Both deployment and pilot
    if (deployCompleted && deploymentIsPilot && pilotCompleted) {
      return false;
    }

    return true;
  };

  /*     USE EFFECT HOOKS     */

  useEffect(() => {
    const incorrect = [];

    Array.from(deploymentSelectedFridges).map((fridge) => {
      if (!(fridge in allTableData) && fridge !== '') {
        incorrect.push(fridge);
      }
    });
    setIncorrectFridgeNames(incorrect);

    if (incorrect.length > 0) {
      setIsDeployDisabled(true);
    } else {
      setIsDeployDisabled(disableDeploy);
    }
  }, [deploymentInfo]);

  // When user goes to /locations/software_version
  useEffect(() => {
    initializeUI();
  }, []);

  // Get all initial table data without filters
  useEffect(() => {
    // After Query Data is loaded
    getAllTableData();
  }, [softwareData]);

  // When all Table Data is generated, produce shown table
  useEffect(() => {
    setSortState({ ...DEFAULT_SORT });
    setShownTableData(Object.values(filteredData));
  }, [filteredData]);

  // When filter returns
  useEffect(() => {
    if (filterResponse) {
      const isVersions = filterResponse.selectedVersions.length > 0;
      const isPilot = Object.keys(filterResponse.selectedPilot).length > 0;
      let isStatusFilter = false;
      Object.keys(filterResponse.statuses).map((key) => {
        if (filterResponse.statuses[key]) {
          isStatusFilter = true;
        }
      });
      if (isVersions || isStatusFilter || isPilot) {
        updateFilteredData();
        setFilterDescription(getFilterDescription(filterResponse));
        setShowClearButton(true);
      } else {
        setFilteredData({ ...allTableData });
        setFilterDescription('FILTER');
        setShowClearButton(false);
      }
    } else {
      setFilteredData({ ...allTableData });
      setFilterDescription('FILTER');
      setShowClearButton(false);
    }
  }, [filterResponse]);

  return (
    <StyledDiv className="container">
      <div className="inventoryHeader">Fridge Software Inventory</div>
      <hr />
      {loading && <Loader />}
      {queryError && (
        <div>
          <h3>
            There was an error querying data, please refresh page and try again.{' '}
          </h3>
        </div>
      )}
      {!loading && !queryError && (
        <div>
          {showDeploymentToolbar && (
            <DeploymentToolbar
              selectedFridges={deploymentSelectedFridges}
              selectedSoftware={deploymentSelectedSoftware}
              versionOptions={deploymentVersionOptions}
              isPilot={deploymentIsPilot}
              deploymentInfo={deploymentInfo}
              setSelectedFridges={setDeploymentSelectedFridges}
              setSelectedSoftware={setDeploymentSelectedSoftware}
              setVersionOptions={setDeploymentVersionOptions}
              setIsPilot={setDeploymentIsPilot}
              setDeploymentInfo={setDeploymentInfo}
              setShowDeploymentToolbar={setShowDeploymentToolbar}
              // isDeployDisabled={isDeployDisabled}
              isDeployDisabled
              setShowConfirmationModal={setShowConfirmationModal}
              disableDeploy={setIsDeployDisabled}
              incorrectFridgeNames={incorrectFridgeNames}
            />
          )}
          <h3>{shownTableData.length} Fridges</h3>
          <div className="toolBar">
            <div className="inputWrapper">
              <input
                type="text"
                name="searchBox"
                id="searchBox"
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'flex-end',
                  padding: '12px 16px',
                  gap: '8px',
                  width: '396px',
                  height: '40px',
                  background: '#EEEEEE',
                  borderRadius: '4px',
                  flex: 'none',
                  order: '1',
                  alignSelf: 'stretch',
                  flexGrow: '0',
                  marginBottom: '0px',
                  border: 'none',
                }}
                onChange={(e) => {
                  setSearchbarValue(e.target.value);
                  setShownTableData(searcher.search(e.target.value));
                }}
                value={searchbarValue}
                placeholder="Search For A Fridge"
              />
            </div>
            <div
              style={{ display: 'flex', alignItems: 'center' }}
              className="filterWrapper"
            >
              <div style={{ paddingRight: '20px' }} className="buttonWrapper">
                <Button
                  style={{
                    height: '40px',
                    borderWidth: '2px',
                    color: '#368007',
                    backgroundColor: '#FFFFFF',
                    borderColor: '#368007',
                    outline: 'none',
                    boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.2)',
                  }}
                  onClick={() => setShowFilter(true)}
                >
                  {filterDescription}
                </Button>
              </div>

              <div style={{ paddingRight: '20px' }} className="buttonWrapper">
                {showClearButton && (
                  <FilterClearButton clearFilter={clearFilter} />
                )}
              </div>

              {/* <div className="buttonWrapper">
                {!showDeploymentToolbar && (
                  <Button
                    style={{
                      height: '40px',
                      border: '2px',
                      color: '#FFFFFF',
                      backgroundColor: '#368007',
                      borderColor: '#368007',
                      outline: 'none',
                      boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.2)',
                    }}
                    onClick={() => {
                      setShowDeploymentToolbar(true);
                      startCreateDeployment();
                    }}
                  >
                    CREATE DEPLOYMENT
                  </Button>
                )}
              </div> */}

              <FilterModal
                show={showFilter}
                onHide={() => setShowFilter(false)}
                softwareData={softwareData}
                pilotsData={pilotsData}
                filterResponse={filterResponse}
                applyFilterResponse={applyFilterResponse}
                updateStatuses={setStatuses}
                updateSelectedSoftware={setSelectedSoftware}
                updateSelectedVersions={setSelectedVersions}
                updateSelectedPilot={setSelectedPilot}
                statuses={statuses}
                selectedSoftware={selectedSoftware}
                selectedVersions={selectedVersions}
                selectedPilot={selectedPilot}
              />
              <ConfirmationModal
                show={showConfirmationModal}
                onHide={() => setShowConfirmationModal(false)}
                isPilot={deploymentIsPilot}
                deploymentInfo={deploymentInfo}
                isDeployDisabled={isDeployDisabled}
                deploySoftware={deploySoftware}
              />
              <ReceiptModal
                show={showDeploymentReceipt}
                onHide={() => setShowDeploymentReceipt(false)}
                deploymentResponse={deploymentResponse}
                failedFridges={failedFridges}
              />
            </div>
          </div>
          <BaseTable
            data={shownTableData}
            width={1148}
            height={600}
            rowClassName={rowClassName}
            sortState={sortState}
            onColumnSort={onColumnSort}
          >
            {showDeploymentToolbar && (
              <Column
                key={Math.random()}
                width={60}
                headerRenderer={headerCheckboxRenderer}
                cellRenderer={checkboxRenderer}
              />
            )}
            <Column
              key="pilots"
              width={103}
              headerRenderer={headerPilotsRenderer}
              cellRenderer={pilotsColumnRenderer}
              sortable
            />
            <Column
              title="Fridge Name"
              key="fridgeName"
              dataKey="fridgeName"
              width={270}
              sortable
            />
            {Object.keys(TABLE_ARTIFACT_COLUMNS).map((artifact) => {
              const title = TABLE_ARTIFACT_COLUMNS[artifact];
              return (
                <Column
                  title={title}
                  key={artifact}
                  dataKey={artifact}
                  width={880 / Object.keys(TABLE_ARTIFACT_COLUMNS).length}
                  sortable
                />
              );
            })}
          </BaseTable>
        </div>
      )}
    </StyledDiv>
  );
};

export default ViewFridgeSoftware;
